import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Shield, AlertTriangle, FileText, Scale } from "lucide-react";

export default function Terminos() {
  return (
    <div className="App">
      <Navbar />
      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="overline text-amber-700">Marco legal</span>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-extrabold tracking-tighter">
            Términos, condiciones y aviso de responsabilidad
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            Al usar DerechoLlave aceptas íntegramente los términos siguientes. Te recomendamos
            leerlos con atención antes de publicar, contactar o comprar un negocio.
          </p>
          <p className="mt-2 text-xs text-slate-400">Última actualización: {new Date().toLocaleDateString("es-CL", { year: "numeric", month: "long" })}</p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-12">

        <Block n="1" icon={<FileText className="w-5 h-5" />} title="Naturaleza de la plataforma">
          <p>
            DerechoLlave es una <strong>plataforma de intermediación digital</strong> que pone a disposición de
            vendedores e inversionistas un entorno protegido para evaluar oportunidades de compraventa
            de negocios en marcha. DerechoLlave <strong>no es propietario, administrador, operador, garante,
            asesor financiero, contable, tributario ni legal</strong> de los negocios publicados.
          </p>
          <p>
            <strong>La plataforma no conecta de forma directa al inversionista con el vendedor.</strong>{" "}
            Toda interacción —solicitudes de información, ofertas, antecedentes, coordinación de visitas y
            cierre— se canaliza a través del <strong>equipo DerechoLlave</strong>, que actúa como único
            interlocutor del inversionista y como contraparte operativa del vendedor. El contacto directo
            entre las partes sólo se habilita una vez que el proceso ha avanzado a las etapas finales
            (visita acompañada o firma de acuerdo) y bajo autorización expresa del equipo.
          </p>
          <p>
            Las etapas del proceso son: <strong>(1) Publicación → (2) Aprobación → (3) Interés del
            inversionista → (4) Entrega de antecedentes → (5) Negociación → (6) Visita acompañada →
            (7) Cierre.</strong> Cada etapa exige acuerdos y validaciones que protegen tanto la
            confidencialidad como la comisión de intermediación.
          </p>
        </Block>

        <Block n="2" icon={<AlertTriangle className="w-5 h-5" />} title="Exclusión de responsabilidad" highlight>
          <p>
            <strong>El comprador acepta que la compra de un negocio en marcha implica riesgo
            comercial.</strong> DerechoLlave:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>No garantiza la veracidad, exactitud, integridad ni vigencia de la información
            publicada por los vendedores (ventas, costos, utilidad, antigüedad, permisos, clientes,
            proveedores, contratos, etc.).</li>
            <li>No garantiza el desempeño futuro del negocio, la continuidad de sus ingresos,
            clientela, proveedores, arriendo, marca, ni de ningún otro componente operativo o
            comercial.</li>
            <li>No responde por pérdidas económicas, lucro cesante, daño emergente, daños
            indirectos, morales o consecuenciales derivados de la decisión de compra, operación
            posterior, conflictos entre las partes, fraudes, ocultamientos o cambios en el mercado.</li>
            <li>No asume responsabilidad por contingencias laborales, tributarias, sanitarias,
            municipales, civiles o penales preexistentes o sobrevinientes al negocio.</li>
            <li>No responde por la idoneidad, solvencia, antecedentes o intenciones de los
            usuarios registrados.</li>
          </ul>
          <p className="font-semibold">
            El comprador es el único responsable de realizar la debida diligencia (due diligence)
            que estime pertinente —legal, contable, tributaria, comercial y operativa— antes de
            concretar cualquier operación.
          </p>
        </Block>

        <Block n="3" icon={<Scale className="w-5 h-5" />} title="Obligaciones del vendedor">
          <ul className="list-disc pl-5 space-y-2">
            <li>Publicar información veraz, actualizada y verificable sobre el negocio.</li>
            <li>No ocultar pasivos, juicios, deudas tributarias o laborales relevantes.</li>
            <li>Subir los antecedentes (balance, contratos, patentes) sólo dentro del repositorio
            privado de la plataforma; nunca por canales externos.</li>
            <li><strong>Reconocer al equipo DerechoLlave como único interlocutor frente al
            inversionista</strong> durante las etapas iniciales (información, antecedentes y oferta).
            Las consultas, observaciones y contraofertas se canalizan a través de la plataforma.</li>
            <li>No solicitar ni aceptar el contacto directo con un inversionista mientras la
            plataforma no lo autorice expresamente (etapa de visita o cierre).</li>
            <li>Responder personalmente por cualquier reclamo derivado de declaraciones falsas o
            incompletas, eximiendo expresamente a DerechoLlave.</li>
          </ul>
        </Block>

        <Block n="4" icon={<Scale className="w-5 h-5" />} title="Obligaciones del inversionista">
          <ul className="list-disc pl-5 space-y-2">
            <li>Verificar de forma independiente toda la información del negocio antes de cerrar la
            compra (contratos, libros contables, permisos, demandas, etc.).</li>
            <li>Realizar las consultas profesionales que estime necesarias (abogado, contador,
            tasador).</li>
            <li><strong>Comunicarse únicamente con el equipo DerechoLlave</strong>: no intentar
            ubicar, contactar ni visitar al vendedor por fuera de la plataforma. El contacto directo
            con el vendedor sólo es posible cuando la plataforma lo autoriza expresamente (visita
            acompañada o firma del acuerdo).</li>
            <li>No solicitar al vendedor antecedentes, documentos ni reuniones por fuera de la
            plataforma. Toda la información se entrega y se audita dentro de DerechoLlave.</li>
            <li>Asumir el riesgo propio de la operación.</li>
          </ul>
        </Block>

        <Block n="5" icon={<Shield className="w-5 h-5" />} title="Confidencialidad y mensajería">
          <p>
            <strong>Toda comunicación se canaliza por la mensajería intermediada de DerechoLlave.</strong>{" "}
            El inversionista conversa exclusivamente con el equipo de la plataforma; el vendedor recibe
            las consultas, ofertas y solicitudes de antecedentes a través del mismo equipo. Ninguna
            parte puede solicitar información de contacto de la otra durante las etapas iniciales.
          </p>
          <p>
            Identidades, teléfonos, correos, direcciones exactas y datos sensibles del negocio se
            mantienen <strong>protegidos por la plataforma</strong> hasta que el proceso avanza a
            visita acompañada o cierre. En esas etapas finales, DerechoLlave puede coordinar y
            autorizar el contacto presencial entre las partes, siempre con acompañamiento y bajo
            los acuerdos de no-circunvención correspondientes.
          </p>
          <p>
            DerechoLlave se reserva el derecho de suspender cuentas que intenten eludir esta
            intermediación.
          </p>
        </Block>

        <Block n="6" icon={<FileText className="w-5 h-5" />} title="Intermediación comercial">
          <p>
            Si la compraventa se concreta a partir de un contacto generado en la plataforma,
            independientemente del canal en que se firme el acuerdo final, las partes reconocen la
            intervención comercial de DerechoLlave y aceptan las condiciones acordadas con la
            plataforma para tal efecto. Los detalles económicos de la intermediación se acuerdan de
            forma privada con cada vendedor al momento de la publicación.
          </p>
        </Block>

        <Block n="7" icon={<AlertTriangle className="w-5 h-5" />} title="Limitación de responsabilidad">
          <p>
            En la máxima medida permitida por la legislación chilena, la responsabilidad total de
            DerechoLlave frente a cualquier usuario por cualquier motivo se limita al monto
            efectivamente cobrado por la plataforma a ese usuario en los 6 meses previos al hecho
            que origina el reclamo, y nunca incluirá daños indirectos, lucro cesante ni daño moral.
          </p>
        </Block>

        <Block n="8" icon={<Scale className="w-5 h-5" />} title="Modificaciones, jurisdicción y contacto">
          <p>
            DerechoLlave puede actualizar estos términos en cualquier momento, publicando la nueva
            versión en este mismo sitio. El uso continuo de la plataforma implica aceptación de las
            modificaciones.
          </p>
          <p>
            Estos términos se rigen por las leyes de la República de Chile. Cualquier controversia
            será sometida a los tribunales ordinarios de justicia con sede en Santiago.
          </p>
          <p>Contacto: <a href="mailto:contacto@derechollave.cl" className="underline">contacto@derechollave.cl</a></p>
        </Block>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <p className="text-sm text-amber-900 leading-relaxed">
            <strong>Importante:</strong> Al registrarte en DerechoLlave, al publicar un negocio o al
            solicitar información, declaras haber leído, comprendido y aceptado estos términos en
            su totalidad, asumiendo personalmente los riesgos asociados a la operación.
          </p>
        </div>

        <div className="text-center pt-8">
          <Link to="/" className="text-sm font-semibold hover:underline">← Volver al inicio</Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}

function Block({ n, icon, title, children, highlight = false }) {
  return (
    <section data-testid={`legal-block-${n}`} className={`${highlight ? "border-2 border-amber-300 bg-amber-50/40" : "border border-zinc-200 bg-white"} rounded-xl p-7`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${highlight ? "bg-amber-600 text-white" : "bg-slate-900 text-white"}`}>
          {icon}
        </div>
        <div className="flex-1">
          <p className="overline text-slate-500">Sección {n}</p>
          <h2 className="mt-1 font-display text-2xl font-bold tracking-tight">{title}</h2>
          <div className="mt-4 space-y-3 text-slate-700 leading-relaxed">{children}</div>
        </div>
      </div>
    </section>
  );
}
